import {
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  SpaceBetween,
} from "@cloudscape-design/components-themed";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../app/hook";
import {
  useGetDivisionQuery,
  useGetMerchantDirectApplicationQuery,
} from "../../features/api";
import { showModal } from "../UI/modal/modal-slice";
import { COMMON_LABELS, merchantTypePattern } from "../default-constants";
import { LABELS } from "./constants";

interface ContractInput {
  agreementOnlyYagoFlag: boolean;
  agreementOnlyFcOrTenantFlag: boolean;
  diffFlag: boolean;
}

/**
 *
 * @returns 加盟店申請（直接）情報
 */
const MerchantDirectApplication = (): JSX.Element => {
  // 受付番号
  const { receiptNumber } = useParams();
  const dispatch = useAppDispatch();

  const {
    contractServiceCode,
    qrPresentationCategory,
    includeYagoFlg,
    includeFcOrTenantFlg,
  } = useGetMerchantDirectApplicationQuery(Number(receiptNumber), {
    skip: !receiptNumber,
    selectFromResult: ({ data }) => ({
      contractServiceCode: data?.contractServiceCode,
      qrPresentationCategory: data?.qrPresentationCategory,
      includeYagoFlg: data?.merchantTypeNos.includes(
        merchantTypePattern.yago.value
      ),
      includeFcOrTenantFlg: data?.merchantTypeNos?.some((type) =>
        [
          merchantTypePattern.yagoFC.value,
          merchantTypePattern.commercialFacility.value,
        ].includes(type)
      ),
    }),
  });

  // 区分取得API呼び出し(親区分コード：3:契約サービス区分コード)
  const { data: divisionData } = useGetDivisionQuery("3");

  // 契約サービス表示用
  const [contractServiceLabel, setContractServiceLabel] = useState("");

  // ユーザ入力データ
  const [input, setInput] = useState<ContractInput>({
    agreementOnlyYagoFlag: false,
    agreementOnlyFcOrTenantFlag: false,
    diffFlag: false,
  });

  /**
   * 契約サービス表示用
   */
  useEffect(() => {
    if (contractServiceCode && divisionData) {
      const contractServiceLabel = divisionData.find(
        (division) => division.kbnCode === contractServiceCode
      )?.label;
      if (contractServiceLabel) setContractServiceLabel(contractServiceLabel);
    }
  }, [contractServiceCode, divisionData]);

  /**
   * 規約ボタンクリック関数
   */
  const handleAgreementBtnOnclick = (name: string) => {
    const keyName = name as keyof ContractInput;
    dispatch(
      showModal({
        title: LABELS.MODAL.TITLE,
        content:
          name === "agreementOnlyYagoFlag"
            ? LABELS.MODAL.TERM_YAGO
            : LABELS.MODAL.TERM_ONLY_FC_OR_TENANT,
        positiveBtn: LABELS.MODAL.OK,
        negativeBtn: LABELS.MODAL.CANCEL,
        positiveFn: () => handleInputOnChange(keyName, true),
        negativeFn: () => handleInputOnChange(keyName, false),
      })
    );
  };

  /**
   * ユーザ入力更新関数
   */
  const handleInputOnChange = useCallback(
    (name: keyof ContractInput, value: boolean) => {
      setInput({ ...input, [name]: value });
    },
    [input]
  );

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <SpaceBetween direction="vertical" size="l">
        <Form>
          <Container header={<Header variant="h2">{LABELS.TITLE}</Header>}>
            <SpaceBetween direction="vertical" size="l">
              <ColumnLayout columns={4}>
                <FormField label={LABELS.CONTRACT_SERVICE}></FormField>
                <div>{contractServiceLabel}</div>
              </ColumnLayout>
              <ColumnLayout columns={4}>
                <FormField label={LABELS.QR}></FormField>
                <div>
                  {qrPresentationCategory === 1
                    ? COMMON_LABELS.OTHER.CPM
                    : COMMON_LABELS.OTHER.MPM}
                </div>
              </ColumnLayout>
              <FormField label={LABELS.PLEASE_CHECK}> </FormField>
              {includeYagoFlg && (
                <FormField label={LABELS.ONLY_YAGO}>
                  <Button
                    variant="link"
                    iconName={
                      input.agreementOnlyYagoFlag
                        ? "status-positive"
                        : "status-negative"
                    }
                    iconAlign="left"
                    onClick={() =>
                      handleAgreementBtnOnclick("agreementOnlyYagoFlag")
                    }
                  >
                    {LABELS.AGREEMENT}
                  </Button>
                </FormField>
              )}

              {includeFcOrTenantFlg && (
                <FormField label={LABELS.ONLY_FC_OR_TENANT}>
                  <Button
                    variant="link"
                    iconName={
                      input.agreementOnlyFcOrTenantFlag
                        ? "status-positive"
                        : "status-negative"
                    }
                    iconAlign="left"
                    onClick={() =>
                      handleAgreementBtnOnclick("agreementOnlyFcOrTenantFlag")
                    }
                  >
                    {LABELS.AGREEMENT}
                  </Button>
                </FormField>
              )}

              <FormField>
                <Checkbox
                  onChange={({ detail }) =>
                    handleInputOnChange("diffFlag", detail.checked)
                  }
                  checked={input.diffFlag}
                >
                  {LABELS.DIFF}
                </Checkbox>
              </FormField>
            </SpaceBetween>
          </Container>
        </Form>

        <ColumnLayout columns={2}>
          <Box float="left">
            <Button
              formAction="none"
              variant="link"
              //todo dummy実装
              onClick={() => alert("back")}
            >
              {COMMON_LABELS.BTNS.BACK}
            </Button>
          </Box>

          <Box float="right">
            <Button
              //todo dummy実装
              onClick={() => alert("next")}
              variant="primary"
              //todo 屋号のみ、FCと商業施設のみの場合bugあり
              disabled={
                (input.diffFlag &&
                  input.agreementOnlyFcOrTenantFlag &&
                  input.agreementOnlyYagoFlag) ||
                (!input.diffFlag &&
                  (!input.agreementOnlyFcOrTenantFlag ||
                    !input.agreementOnlyYagoFlag))
              }
            >
              {COMMON_LABELS.BTNS.NEXT}
            </Button>
          </Box>
        </ColumnLayout>
      </SpaceBetween>
    </form>
  );
};

export default MerchantDirectApplication;
